/**
 * @description This hook provides the event properties used to integrate with the filepond interface and retrieve/process the personnel
 *  file.
 *
 *  When the file is uploaded, the UI will send a request to the backend to read its contents and parse the column headers.  Those
 *  headers are then transformed into a map of column-header/field-id pairs and saved to the form state so it can be used to populate
 *  the rest of the form.
 */

import React from 'react';

import decodeJson from '@seccon/shared/utils/decodeJson';

/**
 * @param {object} param - list of parameters
 * @param {function} param.clearErrors - clears the errors of specific fields; its used to clear the mappingFile errors when the file is removed
 * @param {object} param.error - form error state for the field
 * @param {function} onChange - handles form onChange events; it accepts an array of files uploaded
 * @param {function} setError - sets form errors on a specific field; its used to set filepond errors on the form state
 * @param {function} setHeaderMap - sets the header map state based on the personnel file uploaded or cleared
 * @param {File[]} value - an array of personnel files uploaded
 * @returns {{ onupdatefiles: function }} - event props that need to be added to FilePond component so it can be integrated with the form
 */
const useFilePondEventProps = ({
  clearErrors,
  error,
  onChange,
  setError,
  setHeaderMap,
  value
}) => {
  // This reference allows us to read the error state without reacting to its changes (we only want to react to value state changes)
  const errorRef = React.useRef(error);
  errorRef.current = error;

  /**
   * Handles add file events by processing the file and updating state with a new header map
   * @param {File} file - file to be processed
   */
  function handleAddFile (file) {
    // Prepare the request body
    const data = new FormData();
    data.append('personnelFile', file);

    // Send the file to the backend to process the file and retrieve the column headers.
    // On success, generate the header map and save the new header map state.
    // On failure, set the form error state so the user can be notified.
    fetch('/mapping/processHeaders', {
      method: 'POST',
      body: data
    })
      .then(decodeJson)
      .then((resp) => {
        const newMap = {};

        // Generate a list of field ids that map to each column header.  Note taht we're using generated field ids in the form state
        // rather than the excel headers directly because the header strings could comprise of any number of special characters that
        // might not be suitable for field ids
        for (let index = 0; index < resp.length; index++) {
          newMap[`field-${ index }`] = resp[index];
        }

        setHeaderMap(newMap);
      })
      .catch(({ body: resp }) => {
        setError('mappingFile', {
          type: 'custom',
          message: resp?.message ? `Error: ${ resp.message }` : 'Unable to upload personnel mapping file. Try again later.'
        });
      });
  }

  /**
   * Handles personnel file removal by clearing the header map and any errors on the form field
   */
  function handleRemoveFile () {
    setHeaderMap(null);
    clearErrors('mappingFile');
  }

  /**
   * Handles change events by updating the form field state with the new list of files
   * @param {object[]} fileItems - a list of filepond file items
   */
  function handleChange (fileItems) {
    onChange(
      fileItems.map((entry) => entry.file)
    )
  }

  /**
   * This hook listens for state changes to the field value and triggers the appropriate handler.
   *
   * The reason I'm using a hook rather than using the onaddfile/onremovefile event callbacks from FilePond is because I had a lot
   * of trouble integrating it with react-hook-form.  (1) It was difficult to read/decipher the error state from the fileItems
   * returned from those functions. (2) If I attempted to fetch() after adding a file and then update react state based on the results,
   * I got stuck in an infinite re-render loop with the filepond component.
   *
   * So I decided it was easier/more-stable to just listen for react-hook-form field value changes and run the appropriate callbacks.
   */
  React.useEffect(() => {
    // If the value array is empty, its because a file was removed (or the component was just mounted). Run the remove file handler.
    // Otherwise, this hook was triggered because a file was just uploaded.
    if (value.length === 0) {
      handleRemoveFile();

      return;
    }

    // If an invalid file was uploaded, exit early
    if (errorRef.current) {
      return;
    }

    // Otherwise, run the add file handler.
    handleAddFile(value[0]);
  }, [value]);

  return {
    onupdatefiles: handleChange
  };
}

export default useFilePondEventProps;
